import Vue from 'vue'
import Vuex from 'vuex'
import variables from '@/scss/variables.scss'
import router from '@/router'
import constants from '@/constants'
import { getImageDisplayRatio } from '@/utils/Image'
import { replaceStringFromSpecifcIndex } from '@/utils/String'
import jsBeautify from 'js-beautify'

Vue.use(Vuex)

export default new Vuex.Store({
  strict: true,
  state: {
    status: {
      palette: {
        type: null,
        sectionId: null,
        partsContainerId: null,
      },
      colorPicker: {
        pane: null,
        sectionId: null,
        selector: null,
        prop: null,
      },
      opendLinkChecker: false,
      tinyMceHrefs: [],
      messageToasts: [],
      showDragPartsContainerAssist: false,
      dragPartsContainerActiveSection: null,
      messagePopup: {
        isShow: false,
        isSuccess: false,
        title: '',
        messages: [],
      }
    },
    contentInfo: {},
    contentInfoValidations: {},
    contentTags: [],
    sections: [],
    styles: [],
    mobileStyles: [],
    originalStyle: null,
    originalHeadStyle: null,
    appliedOriginalStyle: null,
    themes: [],
    themeNum: {},
    images: [],
    contentTmpUuid: null,
    contentImages: {},
    contentEditNavibarAction: '',
    scssVars: variables,
    editDiff: {
      isUpdated: false,
      storeHashBeforeEdit: null,
    },
    tagLikeEmailInput: {
      emails: [],
      text: '',
    },
    ecV2PersonalizeItems: [],
  },
  actions: {
    // プレビューや一覧のテスト URL/送信 等用に取得
    async getContent ({ state, commit }, contentId) {
      // フォームバリデーションメッセージのリセット
      commit('updateContentInfoValidations', {})

      const json = await this._vm.$http({
        url: '/content/' + contentId,
        type: 'GET'
      })
      if (json && json.status === 200) {
        const { content, info } = json.data
        commit('updateSections', content.sections)
        commit('updateStyles', content.styles)
        commit('updateMobiles', content.mobileStyles)
        commit('setOriginalStyle', content.originalStyle)
        commit('setOriginalHeadStyle', content.originalHeadStyle)
        commit('mergeContentInfo', {
          ...info,
          folderId: info.folderId || 'unset',
          name: this._vm.$utils.string.b64Decode(info.name),
          preheader: this._vm.$utils.string.b64Decode(info.preheader),
          linkParams: this._vm.$utils.string.b64Decode(info.linkParams),
          memo: this._vm.$utils.string.b64Decode(info.memo),
        })
        commit('updateContentImages', {})
        commit('updateContentTmpUuid', null)
      }
      return json
    },
    // デフォルトメーリングプレビュー
    async getDefaultContent ({ state, commit }, { contentType, contentId }) {
      const json = await this._vm.$http({
        url: `/default_content/${contentType}/${contentId}`,
        type: 'GET'
      })
      if (json && json.status === 200) {
        const { content } = json.data
        commit('updateSections', content.sections)
        commit('updateStyles', content.styles)
        commit('updateMobiles', content.mobileStyles)
        commit('setOriginalStyle', content.originalStyle)
        commit('setOriginalHeadStyle', content.originalHeadStyle)
      }
      return json
    },
    // 編集画面用(叩くごとにtmp画像が生成される)
    async getEditContent ({ state, commit }, contentId) {
      // リセット
      commit('updateEditDiff', {
        isUpdated: false,
        storeHashBeforeEdit: null
      })
      commit('updateContentInfoValidations', {})

      const json = await this._vm.$http({
        url: '/content/edit/' + contentId,
        type: 'GET',
        options: {
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      })
      if (json && json.status === 200) {
        // MySQLから取得
        const { content, info, images, tmpUuid } = json.data
        commit('updateSections', content.sections)
        commit('updateStyles', content.styles)
        commit('updateMobiles', content.mobileStyles)
        commit('setOriginalStyle', content.originalStyle)
        commit('setOriginalHeadStyle', content.originalHeadStyle)
        commit('applyOriginalStyle', {
          originalStyle: content.originalStyle,
        })
        const contentInfo = {
          ...info,
          folderId: info.folderId || 'unset',
          name: this._vm.$utils.string.b64Decode(info.name),
          preheader: this._vm.$utils.string.b64Decode(info.preheader),
          linkParams: this._vm.$utils.string.b64Decode(info.linkParams),
          memo: this._vm.$utils.string.b64Decode(info.memo),
        }
        commit('mergeContentInfo', contentInfo)
        commit('updateContentImages', images)
        commit('updateContentTmpUuid', tmpUuid)
        this._vm.$nextTick(() => {
          // 他のmount処理が走った後に編集差分を作成する
          commit('updateEditDiff', {
            isUpdated: false,
            storeHashBeforeEdit: this._vm.$utils.string.hashCode(
              JSON.stringify({
                contentInfo,
                sections: content.sections,
                styles: content.styles,
                mobileStyles: content.mobileStyles,
                originalStyle: content.originalStyle ? jsBeautify.css(content.originalStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
                originalHeadStyle: content.originalHeadStyle ? jsBeautify.css(content.originalHeadStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
                contentTags: state.contentTags,
              })
            )
          })
        })
      }
      return json
    },
    // 保存
    async savePreviewData ({ state, commit }) {
      const oldState = JSON.parse(JSON.stringify(state))

      const { sections, styles, mobileStyles, originalStyle, originalHeadStyle, contentImages } = state
      if (process.env.VUE_APP_ENV === 'localdev' && [99999].includes(Number(router.currentRoute.params.id))) {
        // 特定のURLはリクエストを送らないように
        return
      }
      // フォームバリデーションメッセージのリセット
      commit('updateContentInfoValidations', {})
      const formData = new FormData()
      const formValidationKeys = ['name', 'preheader', 'link_params', 'memo']
      formValidationKeys.forEach(val => {
        formData.append('formValidationKeys[]', val)
      })
      formData.append('name', this._vm.$utils.string.b64Encode(state.contentInfo.name))
      formData.append('body', JSON.stringify({ sections, styles, mobileStyles, originalStyle, originalHeadStyle }))
      formData.append('folder', state.contentInfo.folderId)
      state.contentTags.forEach(tag => {
        formData.append('tags[]', JSON.stringify({
          'tagId': tag.tagId,
          'contentTagMapId': tag.contentTagMapId,
          'name': this._vm.$utils.string.b64Encode(tag.text)
        }))
      })
      formData.append('preheader', this._vm.$utils.string.b64Encode(state.contentInfo.preheader))
      formData.append('linkParams', this._vm.$utils.string.b64Encode(state.contentInfo.linkParams))
      formData.append('memo', this._vm.$utils.string.b64Encode(state.contentInfo.memo))
      formData.append('size', state.contentInfo.size)
      formData.append('imageSize', state.contentInfo.imageSize)

      for (const section of sections) {
        for (const partsContainer of section.parts_containers) {
          for (const part of partsContainer.parts) {
            // コードパーツにも画像が入る可能性がある
            if (part.type === 'Code') {
              if (part.attrs && part.attrs.length > 0) {
                part.attrs.forEach(attr => {
                  if (attr['data-key']) {
                    formData.append(`images[${attr['data-key']}]`, JSON.stringify(contentImages[attr['data-key']]))
                  }
                })
              }
            } else if (part.type === 'Image' && part.attrs['data-key']) {
              formData.append(`images[${part.attrs['data-key']}]`, JSON.stringify(contentImages[part.attrs['data-key']]))
            }
          }
        }
      }

      if (sections.length === 0) return
      const json = await this._vm.$http({
        url: '/content/save/' + router.currentRoute.params.id,
        type: 'POST',
        params: formData
      })
      if (json && json.status === 200) {
        // save開始時のstate(oldState)を使用することで、Promise解決中の他操作によるstate差分が、保存していないのに編集差分に出てこなくなることを防ぐ
        commit('updateEditDiff', {
          isUpdated: false,
          storeHashBeforeEdit: this._vm.$utils.string.hashCode(
            JSON.stringify({
              contentInfo: oldState.contentInfo,
              sections: oldState.sections,
              styles: oldState.styles,
              mobileStyles: oldState.mobileStyles,
              originalStyle: oldState.originalStyle ? jsBeautify.css(oldState.originalStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
              originalHeadStyle: oldState.originalHeadStyle ? jsBeautify.css(oldState.originalHeadStyle, { indent_size: 2, space_in_empty_paren: true }) : null,
              contentTags: oldState.contentTags,
            })
          )
        })
        commit('showMessageToast', {
          kind: 'success',
          message: '保存しました',
        })
      }
      if (json && json.validations) {
        commit('updateContentInfoValidations', json.validations)
      }
      return json
    },
    deletePartsContainer (context, { sectionId, partsContainerId }) {
      // パレットに表示しているパーツコンテナを削除したら、パレットを初期状態に
      const paletteStatus = context.state.status.palette
      if (paletteStatus.sectionId === sectionId && paletteStatus.partsContainerId === partsContainerId) {
        context.commit('updateStatusPalette', { type: null, sectionId: null, partsContainerId: null })
      }
      // 指定したパーツコンテナを削除 → パーツコンテナの番号を並び替え
      context.commit('deletePartsContainer', { sectionId, partsContainerId })
      context.commit('sortPartsContainers', {})
    },
    // 画像パーツ：画像変更
    reflectImage (context, { sectionId, partsContainerId, partId, styles, attrs }) {
      // 画像を登録 → 表示サイズを調整
      context.commit('updatePart', { sectionId, partsContainerId, partId, styles, attrs })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    // 画像パーツ：スタイル変更
    updateImageStyle (context, { sectionId, partsContainerId, partId, styles }) {
      // スタイルを更新 → 表示サイズを調整
      context.commit('updatePartStyles', { sectionId, partsContainerId, partId, styles })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    addMultiImagePart (context, { sectionId, partsContainerId, imagePosition, oldValue, newValue }) {
      // columnまたはrowを追加 → 画像、テキストボックスのmargin padding調整 → 画像表示サイズを調整
      context.commit('addMultiImagePart', { sectionId, partsContainerId, oldValue, newValue })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    deleteMultiImagePart (context, { sectionId, partsContainerId, oldValue, newValue }) {
      // columnまたはrowを削除 → 画像表示サイズを調整
      context.commit('deleteMultiImagePart', { sectionId, partsContainerId, oldValue, newValue })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateMultiImagePartPosition (context, { sectionId, partsContainerId, imagePartCount, newPosition }) {
      // 画像パーツの位置を変更 → 画像、テキストボックスのmargin padding調整 → 画像表示サイズを調整
      context.commit('updateMultiImagePartPosition', { sectionId, partsContainerId, imagePartCount, newPosition })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateMultiColumnHorizontalDisplayRatio (context, { sectionId, partsContainerId, newRatio }) {
      // 画像テキスト・画像カードパーツコンテナの表示比率を変更
      context.commit('updateMultiColumnHorizontalDisplayRatio', { sectionId, partsContainerId, newRatio })
      // TODO 画像のサイズ変更
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateImagePartsContainerStyles (context, { sectionId, partsContainerId, styles }) {
      // パーツコンテナのスタイルを変更 → 画像表示サイズを調整
      context.commit('updatePartsContainerStyles', { sectionId, partsContainerId, styles })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateImageGroupLayout (context, { sectionId, partsContainerId, newLayout }) {
      // 画像グループのレイアウトを変更 → 画像表示サイズを調整
      context.commit('updateImageGroupLayout', { sectionId, partsContainerId, newLayout })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    // 画像カード：画像パーツの余白を設定
    updateImageCardPartPadding (context, { sectionId, partsContainerId, partId, imagePosition, value }) {
      // 画像の余白を変更 → 画像表示サイズを調整
      context.commit('updateImageCardPartPadding', { sectionId, partsContainerId, partId, imagePosition, value })
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateImageGroupSpacerSize (context, { sectionId, partsContainerId, value }) {
      if (value === '0px') {
        // スペーサーの大きさが0pxの場合：スペーサーを削除
        context.commit('deleteSpacerParts', { sectionId, partsContainerId })
      } else {
        // スペーサーの大きさが0px以外の場合：スペーサーを追加、またはスペーサーの大きさを変更
        context.commit('updateSpacerSize', { sectionId, partsContainerId, value })
      }
      // 画像表示サイズを調整
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateImageTextSpacerSize (context, { sectionId, partsContainerId, value }) {
      if (value === '0px') {
        // スペーサーの大きさが0pxの場合：スペーサーを削除
        context.commit('deleteImageTextSpacerParts', { sectionId, partsContainerId })
      } else {
        // スペーサーの大きさが0px以外の場合：スペーサーを追加、またはスペーサーの大きさを変更
        context.commit('updateImageTextSpacerSize', { sectionId, partsContainerId, value })
      }
      // 画像表示サイズを調整
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateImageGroupColumnCount (context, { sectionId, partsContainerId, value }) {
      // 画像数を変更
      context.commit('updatePartsColumnCount', { sectionId, partsContainerId, value })
      // 画像表示サイズを調整
      context.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
  },
  getters: {
    getScssVars: (state) => {
      return state.scssVars
    },
    noUnitScssVars: (state) => {
      // scssの変数から、pxなどの単位を除去し、キャッシュさせる
      let noUnitScssVars = { ...state.scssVars }
      Object.keys(noUnitScssVars).forEach(key => {
        noUnitScssVars[key] = parseFloat(noUnitScssVars[key].replace(/[^-\d.]/g, ''))
      })
      return noUnitScssVars
    },
    getStatusPalette: (state) => {
      return state.status.palette
    },
    getStatusColorPicker: (state) => {
      return state.status.colorPicker
    },
    getSectionAll: (state) => {
      return state.sections
    },
    getContentInfo: (state) => {
      return state.contentInfo
    },
    getContentInfoValidations: (state) => {
      return state.contentInfoValidations
    },
    getContentInfoPreheader: (state) => {
      return state.contentInfo.preheader ? state.contentInfo.preheader : ''
    },
    getContentInfoLinkParams: (state) => {
      return state.contentInfo.linkParams ? state.contentInfo.linkParams : ''
    },
    getContentInfoMpComment: (state) => {
      // MP 連携時のコメントは最大256文字なので切り取る
      return state.contentInfo.memo ? state.contentInfo.memo.substring(0, constants.maxContentInfoCommentLength) : ''
    },
    getThemes: (state) => {
      return state.themes
    },
    getThemeNum: (state) => {
      return state.themeNum
    },
    getEcV2PersonalizeItems: (state) => {
      return state.ecV2PersonalizeItems
    },
    findPartsContainer: (state) => (sectionId, partsContainerId) => {
      return state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
    },
    getPartsCountOfPartsContainer: (state) => (sectionId, partsContainerId) => {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      // スペーサー以外のパーツ数を返す
      const partsCount = partsContainer ? partsContainer.parts.filter(part => part.type !== 'Spacer').length : 0
      return partsCount
    },
    // パーツの属性を取得
    findPartAttrs: (state) => (sectionId, partsContainerId, partId) => {
      if (!sectionId || !partsContainerId || !partId) return
      return state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
    },
    findContentStyles: (state) => {
      return state.styles
    },
    // ページ全体のモバイルスタイルを、全て取得
    findMobileStyles: (state) => {
      return state.mobileStyles
    },
    // セレクタ単位のスタイルを取得
    findContentStyleAll: (state) => (selector) => {
      const styles = state.styles.filter(style => style.selector === selector)
      let returnStyles = {}
      styles.forEach((style, index) => {
        returnStyles[style.prop] = style.value
      })
      return returnStyles
    },
    findPageStyle: (state) => (selector, prop) => {
      const style = state.styles.find(
        style => style.selector === selector &&
               style.prop === prop
      )
      return style ? style.value : null
    },
    // モバイル：セレクタ単位のスタイルを取得
    findMobileStyleAll: (state) => (selector) => {
      const mobileStyles = state.mobileStyles.filter(mobileStyle => mobileStyle.selector === selector)
      let returnStyles = {}
      mobileStyles.forEach(mobileStyle => {
        returnStyles[mobileStyle.prop] = mobileStyle.value
      })
      return returnStyles
    },
    findSectionStyle: (state) => (sectionName, prop) => {
      const section = state.sections.find(section => section.name === sectionName)
      if (!section) return
      return section.styles[prop]
    },
    findOriginalHeadStyle: (state) => {
      return state.originalHeadStyle
    },
    findAppliedOriginalStyle: (state) => {
      return state.appliedOriginalStyle
    },
    // 全パーツコンテナの最大id
    partsContainersMaxId: (state) => {
      let maxId = state.sections.map(section => {
        return Math.max.apply(
          null,
          section.parts_containers.map(partsContainer => {
            return partsContainer.id
          })
        )
      })
      return Math.max.apply(null, maxId)
    },
    // 全パーツの最大id
    partsMaxId: (state) => {
      let maxId = state.sections.map(section => {
        return Math.max.apply(
          null,
          section.parts_containers.map(partsContainer => {
            return Math.max.apply(
              null,
              partsContainer.parts.map(part => {
                return part.id
              })
            )
          })
        )
      })
      return Math.max.apply(null, maxId)
    },
    findImageById: (state) => (targetId) => {
      const storeImages = state.images
      return storeImages.find((image) => image.id === targetId)
    },
    isValidTagLikeEmailInput: (state) => {
      return state.tagLikeEmailInput.text.length === 0 &&
        state.tagLikeEmailInput.emails.length > 0 &&
        state.tagLikeEmailInput.emails.every(obj => obj.tiClasses.includes('ti-valid'))
    }
  },
  mutations: {
    updateStatusPalette (state, { type, sectionId, partsContainerId }) {
      state.status.palette = {
        type,
        sectionId,
        partsContainerId,
      }
    },
    updateStatusColorPicker (state, { prop, value }) {
      state.status.colorPicker[prop] = value
    },
    updateImages (state, images) {
      if (!images) return
      state.images = images
    },
    updateThemes (state, themes) {
      if (!themes) return
      state.themes = themes
    },
    updateThemeNum (state, themeNum) {
      if (!themeNum) return
      state.themeNum = themeNum
    },
    mergeContentInfo (state, contentInfo) {
      // 基本情報をマージ
      state.contentInfo = { ...state.contentInfo, ...contentInfo }
    },
    mergeImages (state, images) {
      if (!images) return
      const tmpImages = images.slice()
      // 同じkeyの画像は置き換えて、新しい画像は追加する
      state.images.map(stateImage => {
        for (let i = 0, len = images.length; i < len; i++) {
          if (images[i].key === stateImage.key) {
            return tmpImages.shift()
          }
        }
        return stateImage
      })
      state.images = tmpImages.concat(state.images)
    },
    clearStateToLeaveContentEdit (state) {
      // コンテンツ編集を離れる時に不要な情報を削除する
      state.contentInfo = {}
      state.contentInfoValidations = {}
      state.contentTags = []
      state.sections = []
      state.styles = []
      state.mobileStyles = []
      state.originalStyle = null
      state.originalHeadStyle = null
      state.appliedOriginalStyle = null
      state.themes = []
      state.themeNum = {}
      state.contentTmpUuid = null
      state.contentImages = {}
      state.editDiff = {
        isUpdated: false,
        storeHashBeforeEdit: null,
      }
    },
    clearStateToLeaveContentsList (state) {
      // コンテンツ一覧を離れる時に不要な情報を削除する。
      state.contentInfo = {}
      state.contentInfoValidations = {}
      state.contentTags = []
      state.sections = []
      state.styles = []
      state.mobileStyles = []
      state.originalStyle = null
      state.originalHeadStyle = null
      state.themes = []
      state.themeNum = {}
      state.contentTmpUuid = null
      state.contentImages = {}
      state.editDiff = {
        isUpdated: false,
        storeHashBeforeEdit: null,
      }
    },
    updateSections (state, sections) {
      // section display_nameが入っていない場合の一時的な対応 後で消す予定
      sections.forEach(section => {
        if (!section.displayName) {
          if (section.name === 'header') {
            section.displayName = 'ヘッダー'
          } else if (section.name === 'body') {
            section.displayName = 'ボディ'
          } else if (section.name === 'footer') {
            section.displayName = 'フッター'
          } else {
            section.displayName = section.name
          }
        }
      })
      // セクション全て更新
      if (!sections) return
      state.sections = sections
    },
    updateStyles (state, styles) {
      // スタイル全て更新
      if (!styles) return
      state.styles = styles
    },
    updateMobiles (state, mobileStyles) {
      // モバイル用のスタイル全て更新
      if (!mobileStyles) return
      state.mobileStyles = mobileStyles
    },
    updateCodeImage (state, { sectionId, partsContainerId, partId, attrs, oldImageKey }) {
      // 対象のパーツを取得
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
      if (!part) return null
      // attrsがない場合追加する
      if (!part.attrs) {
        Vue.set(part, 'attrs', [])
      }
      if (oldImageKey) {
        // 旧画像キーがある場合:画像の差し替え
        // 対象になるattrを取得
        const updateAttr = part.attrs.find(attr => attr['data-image-key'] === oldImageKey)
        if (updateAttr) {
          // 変更が画面に反映されるよう、setで更新する
          const updateKeys = Object.keys(attrs)
          updateKeys.forEach(updateKey => {
            Vue.set(updateAttr, updateKey, attrs[updateKey])
          })
        } else {
          // コードを他のコードパーツにペーストして画像変更した場合
          part.attrs.push(attrs)
        }
      } else {
        // 旧画像キーがない場合:画像の追加
        part.attrs.push(attrs)
      }
    },
    setOriginalStyle (state, value) {
      if (!value) return
      state.originalStyle = jsBeautify.css(value, { indent_size: 2, space_in_empty_paren: true })
      state.appliedOriginalStyle = value
    },
    setOriginalHeadStyle (state, value) {
      if (!value) return
      state.originalHeadStyle = jsBeautify.css(value, { indent_size: 2, space_in_empty_paren: true })
    },
    // パーツを更新
    updatePart (state, { sectionId, partsContainerId, partId, styles, attrs }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
      if (part) {
        // attrsがない場合もある：なければ追加する
        if (!part.attrs) Vue.set(part, 'attrs', {})

        // SNSアイコンのアイコンスタイルが変更されたら、表示する src を変更する
        if (attrs && part.attrs['data-sns-name']) {
          const iconName = part.attrs['data-sns-name'].toLowerCase() + 'Icon'
          let newSrcType = partsContainer.src_type
          if ((part.attrs['data-sns-name'] === 'LINE') || (part.attrs['data-sns-name'] === 'X' && partsContainer.src_type === 'black')) {
            // LINEはcolorのみ, X(black)はcolorと共通
            newSrcType = 'color'
          }
          attrs['src'] = constants[iconName].url[newSrcType]
        }
        part.styles = { ...part.styles, ...styles }
        part.attrs = { ...part.attrs, ...attrs }
      }
    },
    updatePartBody (state, { sectionId, partsContainerId, partId, body }) {
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
      if (part) part.body = body
    },
    // パーツのスタイルを更新
    updatePartStyles (state, { sectionId, partsContainerId, partId, styles }) {
      if (!sectionId || !partsContainerId || !partId || !styles) return
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      if (!partsContainer) return
      const part = partsContainer.parts.find(part => part.id === partId)
      let updateStyles = {}
      if (part) {
        styles.forEach(style => {
          Vue.set(updateStyles, style.prop, style.value)
        })
        part.styles = { ...part.styles, ...updateStyles }
      }
    },
    // パーツの属性を更新
    updatePartAttrs (state, { sectionId, partsContainerId, partId, attrs }) {
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
      if (part) {
        let updateAttrs = {}

        // attrsがない場合もある：なければ追加する
        if (!part.attrs) Vue.set(part, 'attrs', {})

        attrs.forEach(attr => {
          Vue.set(updateAttrs, attr.prop, attr.value)
          // SNSアイコンの表示タイプが、アイコン＋テキスト以外に変更された場合
          if (attr.prop === 'data-display-sns' && attr.value !== 'IconText') {
            // アイコン＋テキスト専用のレイアウトタイプが設定されていたら、汎用タイプに更新しておく
            const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
            if (partsContainer.layout_type.match(/2row/)) partsContainer.layout_type = partsContainer.layout_type.replace('2row', '')
          }
        })
        part.attrs = { ...part.attrs, ...updateAttrs }
      }
    },
    copyMultiParts (state, { sectionId, partsContainerId, tableId, selectedTab }) {
      // マルチカラムパーツを他のカラムにコピー
      if (!['Content', 'Style'].includes(selectedTab)) return
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      if (!partsContainer || !['Text', 'BoxedText', 'Footer', 'ImageText'].includes(partsContainer.type)) return
      // コピー元のpartsを取得
      const originalParts = partsContainer.parts.filter(part => part.table_id === tableId)
      switch (selectedTab) {
        case 'Content':
          partsContainer.parts.forEach((part, index) => {
            // 自身とスペーサーは除く
            if (part.table_id === tableId || part.type === 'Spacer') return
            // typeが一致するpartを取得
            const originalPart = originalParts.find(originalPart => originalPart.type === part.type)
            Vue.set(partsContainer.parts, index, {
              ...part,
              body: originalPart.body
            })
          })
          break
        case 'Style':
          partsContainer.parts.forEach((part, index) => {
            // 自身とスペーサーは除く
            if (part.table_id === tableId || part.type === 'Spacer') return
            // typeが一致するpartを取得
            const originalPart = originalParts.find(originalPart => originalPart.type === part.type)
            Vue.set(partsContainer.parts, index, {
              ...part,
              styles: originalPart.styles
            })
          })
          break
      }
      // 画像+テキストを更新した場合、画像の表示サイズ調整(崩れるため)
      if (partsContainer.type === 'ImageText') this.commit('updateImagePartDisplaySize', { sectionId, partsContainerId })
    },
    updateSectionPartsContainers (state, { value, sectionId }) {
      // 特定のセクションのパーツコンテナを全て更新
      const section = state.sections.find(section => section.id === sectionId)
      section.parts_containers = value
      // セクション移動が行われたパーツコンテナ：セクションIDを更新する
      const changePartsContainers = section.parts_containers.filter(
        partsContainer => partsContainer.section_id !== sectionId
      )
      changePartsContainers.map(partsContainer => {
        // セクション移動されたパーツがパレットに表示されていたら、パレットのステータスを更新する
        if (state.status.palette.sectionId === partsContainer.section_id && state.status.palette.partsContainerId === partsContainer.id) {
          state.status.palette.sectionId = sectionId
        }
        partsContainer.section_id = sectionId
      })
    },
    updateContentStyles (state, { selector, styles }) {
      // 受け取ったスタイルの分だけ、存在チェック＆更新処理を行う
      styles.map(style => {
        // 更新対象を取得
        let pageStyle = state.styles.find(
          el => el.selector === selector && el.prop === style.prop
        )
        if (pageStyle) {
          // 存在する場合更新
          pageStyle.value = style.value
        } else {
          // 存在しない場合追加
          state.styles.push({ selector: selector, prop: style.prop, value: style.value })
        }
      })
    },
    updateMobileStyles (state, { selector, styles }) {
      // 受け取ったスタイルの分だけ、存在チェック＆更新処理を行う
      styles.map(style => {
        // 更新対象を取得
        let mobileStyle = state.mobileStyles.find(
          el => el.selector === selector && el.prop === style.prop
        )
        if (mobileStyle) {
          // 存在する場合更新
          mobileStyle.value = style.value
        } else {
          // 存在しない場合追加
          state.mobileStyles.push({ selector: selector, prop: style.prop, value: style.value })
        }
      })
    },
    updateOriginalStyle (state, { value }) {
      state.originalStyle = value ? jsBeautify.css(value, { indent_size: 2, space_in_empty_paren: true }) : null
    },
    updateOriginalHeadStyle (state, { value }) {
      state.originalHeadStyle = value ? jsBeautify.css(value, { indent_size: 2, space_in_empty_paren: true }) : null
    },
    applyOriginalStyle (state, { originalStyle }) {
      state.appliedOriginalStyle = originalStyle
    },
    deletePageCssProp (state, { selector, prop }) {
      if (!selector || !prop) return
      const styleIndex = state.styles.findIndex(
        style => style.selector === selector && style.prop === prop
      )
      // 対象のスタイルがある場合のみ削除
      if (styleIndex >= 0) Vue.delete(state.styles, styleIndex)
    },
    updateSectionStyles (state, { sectionName, styles }) {
      if (!sectionName || !styles) return
      // 対象のセクション
      const section = state.sections.find(
        section => section.name === sectionName
      )
      // 受け取ったスタイルの分だけ、存在チェック＆更新処理を行う
      styles.map(style => {
        // 背景色が空欄の場合、設定するカラーコードはtransparent(透明)にする
        if (style.prop === 'background' && !style.value) style.value = 'transparent'
        if (section.styles[style.prop]) {
          // 存在する場合更新
          section.styles[style.prop] = style.value
        } else {
          // 存在しない場合追加
          Vue.set(section.styles, style.prop, style.value)
        }
      })
    },
    appendPartsContainer (state, { sectionId, partsContainerId, newPartsContainer }) {
      // パーツコンテナを複製
      if (!sectionId || !partsContainerId || !newPartsContainer) return
      const section = state.sections.find(section => section.id === sectionId)
      const partsContainerIndex = section.parts_containers.findIndex(partsContainer => partsContainer.id === partsContainerId)
      section.parts_containers.splice(partsContainerIndex + 1, 0, newPartsContainer)
    },
    deletePartsContainer (state, { sectionId, partsContainerId }) {
      if (!sectionId || !partsContainerId) return
      let partsContainers = state.sections.find(section => section.id === sectionId).parts_containers
      // セクションのパーツコンテナが残り1の時削除させない
      if (partsContainers.length === 1) {
        state.status.messagePopup = {
          isShow: true,
          isSuccess: false,
          title: '',
          messages: ['セクションにパーツは最低1つ必要です']
        }
        return
      }
      // パーツコンテナを削除
      const partsContainerIndex = partsContainers.findIndex(partsContainer => partsContainer.id === partsContainerId)
      Vue.delete(partsContainers, partsContainerIndex)
    },
    // パーツコンテナを更新
    updatePartsContainer (state, { sectionId, partsContainerId, values }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      if (partsContainer) {
        values.forEach(value => {
          partsContainer[value.prop.replace(/-/g, '_')] = value.value
        })
      }
    },
    // パーツコンテナのスタイルを更新
    updatePartsContainerStyles (state, { sectionId, partsContainerId, styles }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      if (partsContainer) {
        // スタイルが何も設定されていないとArrayになっている
        // 変更を検知できるようObjectに変更
        if (Array.isArray(partsContainer.styles)) {
          partsContainer.styles = {}
        }
        styles.forEach(style => {
          if (partsContainer.styles[style.prop]) {
            // 存在する場合更新
            partsContainer.styles[style.prop] = style.value
          } else {
            // 存在しない場合追加
            Vue.set(partsContainer.styles, style.prop, style.value)
          }
        })
      }
    },
    sortPartsContainers (state, { addPartsContainer, sectionId }) {
      let orderId = 1
      return state.sections.map(section => {
        return {
          ...section,
          parts: section.parts_containers.map(partsContainer => {
            if (!partsContainer.order_id && !partsContainer.section_id) {
              // パーツの並び順とセクションIDが設定されていない場合：新規に追加されたパーツ
              partsContainer.id = addPartsContainer.id
              partsContainer.type = addPartsContainer.type
              partsContainer.section_id = sectionId
            }
            // order_id の更新(パーツ複製、削除時用)
            partsContainer.order_id = orderId

            orderId++
            return {
              ...partsContainer,
              section_id: section.id,
              order_id: orderId
            }
          })
        }
      })
    },
    // マルチカラム数変更
    updatePartsColumnCount (state, { sectionId, partsContainerId, value }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      // カラム数増加時
      if (partsContainer && partsContainer.column_count < value) {
        // パーツの最大Idを取得
        let partMaxId = Math.max.apply(
          null,
          state.sections.map(section => {
            return Math.max.apply(
              null,
              section.parts_containers.map(partsContainer => {
                return Math.max.apply(
                  null,
                  partsContainer.parts.map(part => {
                    return part.id
                  })
                )
              })
            )
          })
        )
        // パーツコンテナ内最大orderIdを取得
        const columnCount = partsContainer.column_count
        let partOrderId = columnCount // 1つ追加する度に加算していくので、カウント用の値とは別に持っておく
        let tableId = partsContainer.table_count

        // 追加する数だけ回す
        for (let id = columnCount; id < value; id++) {
          // order_id 順にソート
          let parts = partsContainer.parts.sort((a, b) => {
            if (a.order_id < b.order_id) return -1
            if (a.order_id > b.order_id) return 1
            return 0
          })
          // スペーサーを取得：最初の1つだけ
          const spacer = partsContainer.parts.find(part => part.type === 'Spacer')

          // スペーサーがある or カラム数1から増える場合のみ、スペーサーを作成する
          let spacerPart
          if (spacer || partsContainer.column_count === 1) {
            // idとorderIdの最大値を+1する
            partMaxId++
            partOrderId++
            tableId++
            spacerPart = {
              'id': partMaxId,
              'type': 'Spacer',
              'body': null,
              'table_id': Number(tableId),
              'order_id': 1,
              'styles': null,
              'attrs': {
                'width': spacer ? spacer.attrs['width'] : state.scssVars.spacerDefaultWidth,
                'height': spacer ? spacer.attrs['height'] : state.scssVars.spacerDefaultHeight
              },
            }
          }

          // 複製するパートを作成する：最後のパートを取得
          let addPart = JSON.parse(JSON.stringify(parts.slice(-1)[0]))
          // 画像パーツ: 最低限必要な初期値をセット
          // 画像パーツの attrs を初期化：複製元を引き継がないように
          if (addPart.type === 'Image') {
            addPart.attrs = constants.part.image.defaultAttrs
          }
          // idとorderIdの最大値を+1する
          partMaxId++
          partOrderId++
          // 追加するパーツのIDとorderIdをセット
          addPart.id = partMaxId
          addPart.order_id = partOrderId

          // パーツのテーブルID、テーブル数、パーツの並び順を設定
          let tableCount
          // パーツのテーブルID：1つ増やす
          tableId++
          addPart.table_id = Number(tableId)
          // パーツのテーブル数
          // スペーサーあり or カラム数1から増える場合：カラム数 + (カラム数 - 1) ※2カラムパーツなら3、３カラムパーツなら5
          // スペーサーなし：今のカラム数 + 1
          tableCount = (spacer || partsContainer.column_count === 1) ? Number(value) + (Number(value) - 1) : partsContainer.column_count + 1
          // パーツの並び順 = 1（一つのテーブルに1個のパーツ）
          addPart.order_id = 1

          // パーツコンテナの更新
          // スペーサーがある or カラム数1から増える場合のみ、スペーサーを追加する
          if (spacer || partsContainer.column_count === 1) partsContainer.parts.push(spacerPart)
          partsContainer.parts.push(addPart)
          partsContainer.table_count = Number(tableCount)
          partsContainer.column_count += 1
        }
      }
      // カラム数減少時
      if (partsContainer && partsContainer.column_count > value) {
        // 末尾のパーツを削除する回数
        const deleteCount = partsContainer.column_count - value
        // 追加する数だけ回す
        for (let id = 0; id < deleteCount; id++) {
          // order_id 順にソート
          let parts = partsContainer.parts.sort((a, b) => {
            if (a.order_id < b.order_id) return -1
            if (a.order_id > b.order_id) return 1
            return 0
          })
          // スペーサーを取得
          const spacer = partsContainer.parts.find(part => part.type === 'Spacer')
          // 末尾のパーツを削除
          parts.pop()
          // まだパーツが残っていて、スペーサーも存在する場合
          if (parts.length > 1 && spacer) {
            if (spacer) {
              parts.pop()
              // テーブル数を減らす：スペーサー
              partsContainer.table_count -= 1
            }
          }
          partsContainer.column_count -= 1

          // テーブル数を減らす：パーツ
          partsContainer.table_count -= 1
        }
      }
    },
    // 画像テキストパーツ：画像数を増やす
    addMultiImagePart (state, { sectionId, partsContainerId, oldValue, newValue }) {
      // 対象のパーツコンテナを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      if (oldValue === 1) {
        // 画像数1から増える場合

        // テーブルID[2]のパーツ：テーブルID[1] 並び順[2] に変更
        const tableId2Part = partsContainer.parts.find(part => part.table_id === 2)
        tableId2Part.table_id = 1
        tableId2Part.order_id = 2
        // テーブルID[3]のパーツ：テーブルID[1] 並び順[3]に変更
        const tableId3Part = partsContainer.parts.find(part => part.table_id === 3)
        tableId3Part.table_id = 1
        tableId3Part.order_id = 3

        if (partsContainer.layout_type === 'vertical') {
          // 縦レイアウト：パーツコンテナのレイアウトを[横]に変更
          partsContainer.layout_type = 'horizontal'
        } else if (partsContainer.layout_type === 'horizontal') {
          // 横レイアウト：パーツコンテナのレイアウトを[縦]に変更
          partsContainer.layout_type = 'vertical'
        }
      }

      // 共通
      // テーブル数を[画像数 + (画像数 - 1)]、カラム数を[画像数]に変更
      partsContainer.table_count = newValue + (newValue - 1)
      // 横レイアウトの場合、カラム数を[画像数]に変更
      if (partsContainer.layout_type === 'horizontal') partsContainer.column_count = newValue

      // パーツIDの最大値を取得
      let maxPartId = Math.max.apply(
        null,
        state.sections.map(section => {
          return Math.max.apply(
            null,
            section.parts_containers.map(partsContainer => {
              return Math.max.apply(
                null,
                partsContainer.parts.map(part => {
                  return part.id
                })
              )
            })
          )
        })
      )

      // テーブルIDの最大値を取得
      let maxTableId = Math.max.apply(
        null,
        partsContainer.parts.map(part => {
          return part.table_id
        })
      )

      // 画像パーツ: 最低限必要な初期値をセット
      const partImageDefaultAttrs = constants.part.image.defaultAttrs
      const partImageDefaultStyles = constants.part.image.defaultStyles

      // 単体のスペーサーを取得：最初の1つだけ
      const spacer = partsContainer.parts.find(part => part.type === 'Spacer' && part.order_id === 1)

      // 追加する画像数だけループ
      for (let count = oldValue; count < newValue; count++) {
        let spacerPart
        let copyTableId
        // スペーサーがある or カラム数1から増える場合
        if (spacer || oldValue === 1) {
          // スペーサーを作成する：IDとテーブルIDの最大値を+1する
          maxPartId++
          maxTableId++
          spacerPart = {
            'id': maxPartId,
            'type': 'Spacer',
            'body': null,
            'table_id': maxTableId,
            'order_id': 1,
            'styles': null,
            'attrs': {
              'width': spacer ? spacer.attrs['width'] : state.scssVars.spacerDefaultWidth,
              'height': spacer ? spacer.attrs['height'] : state.scssVars.spacerDefaultHeight
            }
          }
          // スペーサーでテーブルIDが1つ進んでいるので、取得したいテーブルIDは[最大テーブルID - 1]
          copyTableId = maxTableId - 1
        } else {
          copyTableId = maxTableId
        }
        // 複製するパーツを取得
        const addPart1 = JSON.parse(JSON.stringify(partsContainer.parts.find(
          part => part.table_id === copyTableId && part.order_id === 1
        )))
        const addPart2 = JSON.parse(JSON.stringify(partsContainer.parts.find(
          part => part.table_id === copyTableId && part.order_id === 2
        )))
        const addPart3 = JSON.parse(JSON.stringify(partsContainer.parts.find(
          part => part.table_id === copyTableId && part.order_id === 3
        )))

        // パーツ1とパーツ2：テーブルIDを増やす
        maxTableId++
        addPart1.table_id = maxTableId
        addPart2.table_id = maxTableId
        addPart3.table_id = maxTableId

        // パーツ1：IDを増やす
        maxPartId++
        addPart1.id = maxPartId

        // パーツ2：IDを増やす
        maxPartId++
        addPart2.id = maxPartId

        // パーツ3：IDを増やす
        maxPartId++
        addPart3.id = maxPartId

        // 画像パーツの [attrs, styles] を初期化：複製元を引き継がないように
        if (addPart1.type === 'Image') {
          addPart1.attrs = partImageDefaultAttrs
          addPart1.styles = partImageDefaultStyles
        } else if (addPart2.type === 'Image') {
          addPart2.attrs = partImageDefaultAttrs
          addPart2.styles = partImageDefaultStyles
        } else if (addPart3.type === 'Image') {
          addPart3.attrs = partImageDefaultAttrs
          addPart3.styles = partImageDefaultStyles
        }

        // パーツコンテナの末尾に追加
        if (spacerPart) partsContainer.parts.push(spacerPart)
        if (addPart1) partsContainer.parts.push(addPart1)
        if (addPart2) partsContainer.parts.push(addPart2)
        if (addPart3) partsContainer.parts.push(addPart3)
      }
    },
    // 画像テキスト：画像数を減らす
    deleteMultiImagePart (state, { sectionId, partsContainerId, oldValue, newValue }) {
      // 対象のパーツコンテナを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      // テーブルIDの最大値を取得
      let maxTableId = Math.max.apply(
        null,
        partsContainer.parts.map(part => {
          return part.table_id
        })
      )
      // tableID orderID 順にソート
      partsContainer.parts.sort((a, b) => {
        if (a.table_id < b.table_id) return -1
        if (a.table_id > b.table_id) return 1
        if (a.order_id < b.order_id) return -1
        if (a.order_id > b.order_id) return 1
        return 0
      })
      // 単体のスペーサーパーツを取得
      const spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer' && part.order_id === 1)

      let deletePartsCount = 0
      // 削除する画像数だけループ
      for (let count = newValue; count < oldValue; count++) {
        // 削除対象のパーツを取得し、その分だけ削除カウントを増やす
        const deleteParts = partsContainer.parts.filter(part => part.table_id === maxTableId)
        deletePartsCount = deletePartsCount + deleteParts.length
        // テーブルが1つ削除されるので、最大値を -1
        maxTableId--
        // 単体のスペーサーがある場合
        if (spacerParts.length > 0) {
          // 削除対象のスペーサーを取得し、その分だけ削除カウントを増やす
          const deleteSpacerParts = partsContainer.parts.filter(part => part.table_id === maxTableId && part.type === 'Spacer')
          deletePartsCount = deletePartsCount + deleteSpacerParts.length
          // テーブルが1つ削除されるので、最大値を -1
          maxTableId--
        }
      }

      // 削除カウントの数だけ、末尾のパーツを削除
      for (let count = 0; count < deletePartsCount; count++) {
        partsContainer.parts.pop()
      }

      if (newValue === 1) {
        // 画像数：1に減る場合

        // テーブル数：3に変更
        partsContainer.table_count = 3
        // テーブルID[1] 並び順[2]のパーツ：テーブルID[2] 並び順[1]に変更
        const tableId1OrderId2Part = partsContainer.parts.find(part => part.table_id === 1 && part.order_id === 2)
        tableId1OrderId2Part.table_id = 2
        tableId1OrderId2Part.order_id = 1
        // テーブルID[1] 並び順[3]のパーツ：テーブルID[3] 並び順[1]に変更
        const tableId1OrderId3Part = partsContainer.parts.find(part => part.table_id === 1 && part.order_id === 3)
        tableId1OrderId3Part.table_id = 3
        tableId1OrderId3Part.order_id = 1

        if (partsContainer.layout_type === 'vertical') {
          // 縦レイアウト：パーツコンテナのレイアウトを横に変更
          partsContainer.layout_type = 'horizontal'
        } else if (partsContainer.layout_type === 'horizontal') {
          // 横レイアウト：パーツコンテナのレイアウトを縦、カラム数[画像数]に変更
          partsContainer.layout_type = 'vertical'
          partsContainer.column_count = newValue
          // モバイル表示も縦並びのみのため横並びフラグを落とす
          partsContainer.flg_side = 0
        }
      } else {
        // 画像数：2以上に減る場合

        // テーブル数：現在の、最大のテーブルID
        partsContainer.table_count = maxTableId
        // 横レイアウトの場合：カラム数を[画像数]に変更
        if (partsContainer.layout_type === 'horizontal') partsContainer.column_count = newValue
      }
    },
    // 画像テキスト、画像カードパーツ：表示比率を変更
    updateMultiColumnHorizontalDisplayRatio (state, { sectionId, partsContainerId, newRatio }) {
      if (!sectionId || !partsContainerId || !newRatio) return

      // 対象のパーツコンテナを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      partsContainer.horizontal_display_ratio = newRatio
    },
    // 画像テキスト、画像カードパーツ：画像パーツの位置を変更
    updateMultiImagePartPosition (state, { sectionId, partsContainerId, imagePartCount, newPosition }) {
      // 引数がどれか1つでも足りなかったら何もしない
      if (!sectionId || !partsContainerId || !imagePartCount || !newPosition) return

      // 対象のパーツコンテナを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      if (imagePartCount === 1) {
        // 画像数[1]の場合

        // 画像パーツ、テキスト(ボックス)パーツ：それぞれ個別に取得
        const imagePart = partsContainer.parts.find(part => part.type === 'Image')
        const textPart = partsContainer.parts.find(part => part.type === 'Text' || part.type === 'BoxedText')

        if (newPosition === 'Top') {
          // ポジションが[Top]
          // パーツコンテナ：レイアウトを[縦]、カラム数を[1]に変更
          partsContainer.layout_type = 'vertical'
          partsContainer.column_count = 1
          // 画像パーツ：テーブルIDを[1]に変更
          imagePart.table_id = 1
          // テキスト(ボックス)パーツ：画像カードならテーブルIDを[2]、画像テキストならテーブルIDを[3]に変更
          if (partsContainer.type === 'ImageCard') {
            textPart.table_id = 2
          } else {
            textPart.table_id = 3
          }
          // 表示比率を1:1にする
          partsContainer.horizontal_display_ratio = 'imageOneToTextOne'
          // モバイル表示：縦並びにする
          partsContainer.flg_side = 0
        } else if (newPosition === 'Bottom') {
          // ポジションが[Bottom]
          // パーツコンテナ：レイアウトを[縦]、カラム数を[1]に変更
          partsContainer.layout_type = 'vertical'
          partsContainer.column_count = 1
          // 画像パーツ：画像カードならテーブルIDを[2]、画像テキストならテーブルIDを[3]に変更
          if (partsContainer.type === 'ImageCard') {
            imagePart.table_id = 2
          } else {
            imagePart.table_id = 3
          }
          // テキスト(ボックス)パーツ：テーブルIDを[1]に変更
          textPart.table_id = 1
          // 表示比率を1:1にする
          partsContainer.horizontal_display_ratio = 'imageOneToTextOne'
          // モバイル表示：縦並びにする
          partsContainer.flg_side = 0
        } else if (newPosition === 'Left') {
          // ポジションが[Left]
          // パーツコンテナ：レイアウトを[横]、カラム数を[2]に変更
          partsContainer.layout_type = 'horizontal'
          partsContainer.column_count = 2
          // 画像パーツ：テーブルIDを[1]に変更
          imagePart.table_id = 1
          // テキスト(ボックス)パーツ：画像カードならテーブルIDを[2]、画像テキストならテーブルIDを[3]に変更
          textPart.table_id = partsContainer.type === 'ImageCard' ? 2 : 3
        } else if (newPosition === 'Right') {
          // ポジションが[Right]
          // パーツコンテナ：レイアウトを[横]、カラム数を[2]に変更
          partsContainer.layout_type = 'horizontal'
          partsContainer.column_count = 2
          // 画像パーツ：画像カードならテーブルIDを[2]、画像テキストならテーブルIDを[3]に変更
          imagePart.table_id = partsContainer.type === 'ImageCard' ? 2 : 3
          // テキスト(ボックス)パーツ：テーブルIDを[1]に変更
          textPart.table_id = 1
        }
      } else if (imagePartCount >= 2) {
        // 画像数[2以上]の場合

        // パーツコンテナの設定
        if (newPosition === 'Top' || newPosition === 'Bottom') {
          // ポジションが[Top][Bottom]：レイアウトを[横]、カラム数を[画像数]に変更
          partsContainer.layout_type = 'horizontal'
          partsContainer.column_count = imagePartCount
          // 表示比率を1:1にする
          partsContainer.horizontal_display_ratio = 'imageOneToTextOne'
        } else if (newPosition === 'Right' || newPosition === 'Left') {
          // ポジションが[Right][Left]：レイアウトを[縦]、カラム数を[2]に変更
          partsContainer.layout_type = 'vertical'
          partsContainer.column_count = 2
        }

        // パーツコンテナ内にある、テーブルの数だけループ
        for (let count = 1; count <= partsContainer.table_count; count++) {
          // 画像パーツ、テキスト(ボックス)パーツ：それぞれ個別に取得
          const imagePart = partsContainer.parts.find(part => part.table_id === count && part.type === 'Image')
          const textPart = partsContainer.parts.find(part => part.table_id === count && (part.type === 'Text' || part.type === 'BoxedText'))

          // 画像パーツ、テキスト(ボックス)パーツがあった場合のみ
          // スペーサーのみのテーブルであれば、何もしない
          if (imagePart && textPart) {
            // パーツの設定
            if (newPosition === 'Top' || newPosition === 'Left') {
              // ポジションが[Top] または [Left]
              // 画像パーツ：並び順を[1]に変更
              // テキスト(ボックス)パーツ：画像カードなら並び順を[2]、画像テキストなら並び順を[3]に変更
              imagePart.order_id = 1
              textPart.order_id = partsContainer.type === 'ImageCard' ? 2 : 3
            } else if (newPosition === 'Bottom' || newPosition === 'Right') {
              // ポジションが[Bottom] または [Right]
              // 画像パーツ：画像カードなら並び順を[2]、画像テキストなら並び順を[3]に変更
              // テキスト(ボックス)パーツ：並び順を[1]に変更
              imagePart.order_id = partsContainer.type === 'ImageCard' ? 2 : 3
              textPart.order_id = 1
            }
          }
        }
      }
    },
    // 画像テキスト、画像カードパーツ：画像の表示サイズ調整
    updateImagePartDisplaySize (state, { sectionId, partsContainerId }) {
      // 対象のパーツを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      const imageParts = partsContainer.parts.filter(part => part.type === 'Image')
      imageParts.forEach(imagePart => {
        // パーツコンテナの左右余白を取得
        const partsContainerPaddingRight = partsContainer.styles['padding-right'] ? Number(partsContainer.styles['padding-right'].replace('px', '')) : 0
        const partsContainerPaddingLeft = partsContainer.styles['padding-left'] ? Number(partsContainer.styles['padding-left'].replace('px', '')) : 0
        const partsContainerPadding = partsContainerPaddingRight + partsContainerPaddingLeft

        // 画像パーツの左右余白を取得
        const imagePaddingRight = imagePart.styles['padding-right'] ? Number(imagePart.styles['padding-right'].replace('px', '')) : 0
        const imagePaddingLeft = imagePart.styles['padding-left'] ? Number(imagePart.styles['padding-left'].replace('px', '')) : 0
        const imagePadding = imagePaddingRight + imagePaddingLeft

        // スペーサーの合計幅
        let spacerWidth = 0
        let spacerParts
        if (partsContainer.layout_type === 'horizontal' && partsContainer.type !== 'ImageText') {
          // 横レイアウトで、画像テキスト以外：全スペーサーを取得
          spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer')
        } else if (partsContainer.layout_type === 'horizontal' && partsContainer.type === 'ImageText') {
          // 横レイアウトで、画像テキスト
          const imageParts = partsContainer.parts.filter(part => part.type === 'Image')
          if (imageParts.length === 1) {
            // 画像数が1：全スペーサーを取得
            spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer')
          } else if (imageParts.length >= 2) {
            // 画像数が2以上：単体で表示されているスペーサーを取得
            spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer' && part.order_id === 1)
          }
        } else if (partsContainer.layout_type === 'vertical' && partsContainer.type === 'ImageText') {
          // 縦レイアウトで、画像テキスト
          const imageParts = partsContainer.parts.filter(part => part.type === 'Image')
          // 画像数が2以上：代表で、1行目のスペーサーを取得
          // 全行のスペーサーの幅は同じ
          if (imageParts.length >= 2) {
            spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer' && part.table_id === 1)
          }
        }
        // 取得したスペーサーの幅を合計する
        if (spacerParts && spacerParts.length > 0) {
          spacerParts.forEach(spacerPart => {
            spacerWidth += spacerPart.attrs.width ? Number(spacerPart.attrs.width.replace('px', '')) : 0
          })
        }

        let partWidth
        const contentsWidth = Number(state.scssVars.contentsWidth.replace('px', ''))
        if (partsContainer.type === 'ImageCard' && partsContainer.layout_type === 'horizontal') {
          // 画像カード横レイアウトで表示比率を反映した画像サイズ幅を計算
          const imageDisplayRatio = getImageDisplayRatio(partsContainer)
          partWidth = ((contentsWidth - partsContainerPadding - spacerWidth) * imageDisplayRatio).toFixed(3)
        } else if (partsContainer.layout_type === 'horizontal' && partsContainer.type === 'ImageText') {
          // 横レイアウトで、画像テキスト
          const imageParts = partsContainer.parts.filter(part => part.type === 'Image')
          if (imageParts.length === 1) {
            // 表示比率を反映した画像サイズ幅を計算
            const imageDisplayRatio = getImageDisplayRatio(partsContainer)
            partWidth = ((contentsWidth - partsContainerPadding - spacerWidth) * imageDisplayRatio).toFixed(3)
          } else {
            // パーツ1個の幅 = ( メールの幅 - パーツコンテナ左右の余白 - スペーサーの合計幅 ) / カラム数
            partWidth = (contentsWidth - partsContainerPadding - spacerWidth) / partsContainer.column_count
          }
        } else if (partsContainer.layout_type === 'vertical' && partsContainer.type === 'ImageText') {
          // 縦レイアウトで、画像テキスト
          const imageParts = partsContainer.parts.filter(part => part.type === 'Image')
          if (imageParts.length === 1) {
            // パーツ1個の幅 = ( メールの幅 - パーツコンテナ左右の余白 - スペーサーの合計幅 ) / カラム数
            partWidth = (contentsWidth - partsContainerPadding - spacerWidth) / partsContainer.column_count
          } else {
            // 表示比率を反映した画像サイズ幅を計算
            const imageDisplayRatio = getImageDisplayRatio(partsContainer)
            partWidth = ((contentsWidth - partsContainerPadding - spacerWidth) * imageDisplayRatio).toFixed(3)
          }
        } else if (partsContainer.type === 'ImageGroup') {
          if (partsContainer.layout_type === 'vertical') {
            partWidth = (contentsWidth - partsContainerPadding - spacerWidth)
          } else {
            partWidth = (contentsWidth - partsContainerPadding - spacerWidth) / partsContainer.column_count
          }
        } else if (partsContainer.type === 'Image' || partsContainer.type === 'Logo' || partsContainer.type === 'ImageCard') {
          // パーツ1個の幅 = ( メールの幅 - パーツコンテナ左右の余白 - スペーサーの合計幅 ) / カラム数
          partWidth = (contentsWidth - partsContainerPadding - spacerWidth) / partsContainer.column_count
        } else {
          // それ以外：コンテンツ幅 / カラム数 の値を、パーツの幅とする
          partWidth = contentsWidth / partsContainer.column_count
        }

        // パーツコンテナの罫線幅を取得
        const partsContainerBorder = partsContainer.styles['border'] ? partsContainer.styles['border'].split(' ')[1] : null
        let partsContainerBorderWidth = 0
        if (partsContainerBorder) {
          // 縦レイアウトの時は左右2つの罫線、横レイアウトの時は左または右の1つの罫線で判定
          partsContainerBorderWidth = partsContainer.layout_type === 'vertical' ? Number(partsContainerBorder.replace('px', '')) * 2 : Number(partsContainerBorder.replace('px', ''))
        }

        // 画像パーツの罫線幅を取得
        // スタイルは border 1px solid の形になっているので、インデックス1番が線の太さ
        const imageBorderRight = imagePart.styles['border-right'] ? imagePart.styles['border-right'].split(' ')[1] : null
        const imageBorderLeft = imagePart.styles['border-left'] ? imagePart.styles['border-left'].split(' ')[1] : null

        // 罫線のスタイルの、太さ部分を数値に変換する
        const imageBorderRightWidth = imageBorderRight ? Number(imageBorderRight.replace('px', '')) : 0
        const imageBorderLeftWidth = imageBorderLeft ? Number(imageBorderLeft.replace('px', '')) : 0
        const imageBorderWidth = imageBorderRightWidth + imageBorderLeftWidth

        // 元画像の大きさを取得
        const baseWidth = imagePart.attrs['data-width'] ? Number(imagePart.attrs['data-width'].replace('px', '')) : constants.emptyImage.width
        const baseHeight = imagePart.attrs['data-height'] ? Number(imagePart.attrs['data-height'].replace('px', '')) : constants.emptyImage.height

        // 画像の表示倍率を取得：未設定は100％とする
        const percent = imagePart.attrs['data-display-percent'] ? imagePart.attrs['data-display-percent'] : 100

        // 余白を含めた幅がパーツの幅を超えた場合、画像の比率を変更する
        let imageDisplayWidth = baseWidth + imagePadding + imageBorderWidth + partsContainerBorderWidth > partWidth ? partWidth - imagePadding - imageBorderWidth - partsContainerBorderWidth : baseWidth
        let imageDisplayHeight = baseWidth + imagePadding + imageBorderWidth + partsContainerBorderWidth > partWidth ? baseHeight * (partWidth - imagePadding - imageBorderWidth - partsContainerBorderWidth) / baseWidth : baseHeight
        imageDisplayWidth *= percent / 100
        imageDisplayHeight *= percent / 100

        imagePart.attrs['width'] = Math.ceil(imageDisplayWidth) + 'px'
        imagePart.attrs['height'] = Math.ceil(imageDisplayHeight) + 'px'
      })
    },
    // SNSアイコンの追加
    addSnsIcon (state, { sectionId, partsContainerId, newSnsIcon }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      // 最後のパーツをコピー
      const addPart = JSON.parse(JSON.stringify(partsContainer.parts.slice(-1)[0]))
      // 参照渡しで値が変わるため、サイズのみ個別で取得
      const size = partsContainer.parts.slice(-1)[0].attrs.width

      // パーツの最大Idを取得
      let maxPartId = Math.max.apply(
        null,
        state.sections.map(section => {
          return Math.max.apply(
            null,
            section.parts_containers.map(partsContainer => {
              return Math.max.apply(
                null,
                partsContainer.parts.map(part => {
                  return part.id
                })
              )
            })
          )
        })
      )

      // テーブルIDの最大値を取得
      let maxTableId = Math.max.apply(
        null,
        partsContainer.parts.map(part => {
          return part.table_id
        })
      )

      // パーツID、テーブルID：最大値+1
      maxPartId++
      maxTableId++
      addPart.id = maxPartId
      addPart.table_id = maxTableId

      const iconName = newSnsIcon.attrs['data-sns-name'].toLowerCase() + 'Icon'
      let srcType = partsContainer.src_type
      if ((newSnsIcon.attrs['data-sns-name'] === 'LINE') || (newSnsIcon.attrs['data-sns-name'] === 'X' && partsContainer.src_type === 'black')) {
        // LINEはcolorのみ,X(black)はcolorと共通
        srcType = 'color'
      }
      // attrsを上書きして、パーツコンテナに追加
      addPart.attrs = {
        ...newSnsIcon.attrs,
        width: size,
        height: size,
        src: constants[iconName].url[srcType],
        'data-display-sns': addPart.attrs['data-display-sns'],
      }

      if (partsContainer.type === 'Share') {
        // シェアボタンの場合
        // リンクに関する項目は最初のパーツと同じ値にする
        const firstPart = partsContainer.parts[0]
        addPart.attrs.href = firstPart.attrs.href
        addPart.attrs.title = firstPart.attrs.title
        addPart.attrs.target = firstPart.attrs.target
        // altテキストはシェアボタン用の初期値とする
        addPart.attrs.alt = newSnsIcon.attrs['data-share-alt']
      }
      partsContainer.parts.push(addPart)

      // カラム数
      if (partsContainer.layout_type.match(/horizontal/)) {
        // 横レイアウト：パーツ数に合わせる
        partsContainer.column_count = partsContainer.parts.length
      } else if (partsContainer.layout_type.match(/vertical/)) {
        // 縦レイアウト：1で固定
        partsContainer.column_count = 1
      }

      // テーブル数：パーツ数に合わせる
      partsContainer.table_count = partsContainer.parts.length
    },
    // フォローボタン、シェアボタン：SNSアイコンパーツを削除
    deleteSNSIconPart (state, { sectionId, partsContainerId, partId }) {
      // 対象のパーツコンテナを取得
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      // 対象パーツを、パーツの配列から削除
      const partIndex = partsContainer.parts.findIndex(part => part.id === partId)
      Vue.delete(partsContainer.parts, partIndex)

      // カラム数の更新：横並びレイアウトの場合、パーツ数に合わせる
      if (partsContainer.layout_type === 'horizontal') {
        partsContainer.column_count = partsContainer.parts.length
      }

      // テーブル数の更新：パーツ数に合わせる
      partsContainer.table_count = partsContainer.parts.length

      // テーブルIDの更新：パーツの上から順に振り直す
      let tableCount = 1
      partsContainer.parts.forEach(part => {
        part.table_id = tableCount
        tableCount++
      })
    },
    // SNSアイコンの変更
    updateSnsIcon (state, { sectionId, partsContainerId, partId, newSnsIcon }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)
      // アイコンスタイルを取得
      const iconName = newSnsIcon.attrs['data-sns-name'].toLowerCase() + 'Icon'
      let srcType = partsContainer.src_type
      if ((newSnsIcon.attrs['data-sns-name'] === 'LINE') || (newSnsIcon.attrs['data-sns-name'] === 'X' && partsContainer.src_type === 'black')) {
        // LINEはcolorのみ, X(black)はcolorと共通
        srcType = 'color'
      }

      const updateSnsIcon = newSnsIcon

      if (partsContainer.type === 'Share') {
        // シェアボタンの場合
        // リンクに関する項目は既存パーツの値を引き継ぐ
        updateSnsIcon.attrs.href = part.attrs.href
        updateSnsIcon.attrs.title = part.attrs.title
        updateSnsIcon.attrs.target = part.attrs.target
        // altテキストはシェアボタン用の初期値とする
        updateSnsIcon.attrs.alt = newSnsIcon.attrs['data-share-alt']
      }
      // attrsを上書きする
      part.attrs = {
        ...updateSnsIcon.attrs,
        width: part.attrs['width'],
        height: part.attrs['height'],
        src: constants[iconName].url[srcType],
        'data-display-sns': part.attrs['data-display-sns'],
      }
    },
    // SNSアイコンの並び替え
    sortSnsIcon (state, { sectionId, partsContainerId, parts }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      partsContainer.parts = parts
      let tableCount = 1
      partsContainer.parts.forEach(part => {
        part.table_id = tableCount
        tableCount++
      })
    },
    // SNS アイコンのアイコンタイプ変更
    updateSnsIconSrcType (state, { sectionId, partsContainerId, partId, newSnsIcon, newSrcType }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      const part = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId).parts.find(part => part.id === partId)

      // アイコンスタイルを取得
      const iconName = newSnsIcon.attrs['data-sns-name'].toLowerCase() + 'Icon'
      let srcType = newSrcType
      if ((newSnsIcon.attrs['data-sns-name'] === 'LINE') || (newSnsIcon.attrs['data-sns-name'] === 'X' && newSrcType === 'black')) {
        // LINEはcolorのみ,X(black)はcolorと共通
        srcType = 'color'
      }

      // アイコンスタイルを上書きする
      partsContainer.src_type = newSrcType
      part.attrs.src = constants[iconName].url[srcType]
    },
    updateImageGroupLayout (state, { sectionId, partsContainerId, newLayout }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      switch (newLayout) {
        case 'image2-vertical':
          // 縦
          partsContainer.layout_type = 'vertical'
          // モバイル表示：横並びを解除
          partsContainer.flg_side = 0
          break
        case 'image2-horizontal':
          // 横
          partsContainer.layout_type = 'horizontal'
          break
      }
    },
    updateSnsIconLayout (state, { sectionId, partsContainerId, newLayout }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      partsContainer.layout_type = newLayout
      if (newLayout.match(/vertical/)) {
        // 縦レイアウト：カラム数 = 1
        partsContainer.column_count = 1
      } else if (newLayout.match(/horizontal/)) {
        // 横レイアウト：カラム数 = パーツ数
        partsContainer.column_count = partsContainer.parts.length
      }
    },
    updateImageCardPartPadding (state, { sectionId, partsContainerId, partId, imagePosition, value }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      if (!partsContainer) return null
      const part = partsContainer.parts.find(part => part.id === partId)
      if (!part) return null

      const padding = value === '0px' ? null : value
      let updateStyles = {}
      for (let key of ['padding-top', 'padding-right', 'padding-bottom', 'padding-left']) {
        updateStyles[key] = padding
      }
      part.styles = { ...part.styles, ...updateStyles }
    },
    // スペーサーを削除：画像テキスト
    deleteImageTextSpacerParts (state, { sectionId, partsContainerId }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      // パーツコンテナから、スペーサーを削除
      partsContainer.parts.forEach(part => {
        if (part.type === 'Spacer' && part.order_id === 1) {
          const spacerPartIndex = partsContainer.parts.findIndex(spacerPart => spacerPart.id === part.id)
          Vue.delete(partsContainer.parts, spacerPartIndex)
        }
      })

      // テーブルIDを振り直す
      // 今のテーブルID - (今の選択テーブルId / 2)に変更
      // テーブルID[5]の場合 → 5 - (5 / 2) = テーブルID[3]になる
      partsContainer.parts.forEach(part => {
        if (part.table_id !== 1) {
          const subNumber = Math.floor(Number(part.table_id) / 2)
          part.table_id = Number(part.table_id) - subNumber
        }
      })

      // テーブル数の更新：最大のテーブルIDとする
      const maxTableId = Math.max.apply(
        null,
        partsContainer.parts.map(part => {
          return part.table_id
        })
      )
      partsContainer.table_count = maxTableId
    },
    // スペーサーを削除：画像グループ、テキスト、テキストボックス、フッター
    deleteSpacerParts (state, { sectionId, partsContainerId }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)

      // table_id 順にソート
      partsContainer.parts.sort((a, b) => {
        if (a.table_id < b.table_id) return -1
        if (a.table_id > b.table_id) return 1
        return 0
      })

      // パーツコンテナから、スペーサーを削除
      partsContainer.parts.forEach(part => {
        if (part.type === 'Spacer') {
          const spacerPartIndex = partsContainer.parts.findIndex(spacerPart => spacerPart.id === part.id)
          Vue.delete(partsContainer.parts, spacerPartIndex)
        }
      })
      // 上から順に、テーブルIDを振り直す
      let tableId = 1
      partsContainer.parts.forEach(part => {
        part.table_id = tableId
        tableId++
      })
      // テーブル数を更新：テーブル数 = パーツ数
      partsContainer.table_count = partsContainer.parts.length
    },
    // スペーサーの大きさを更新：画像グループ、テキスト、テキストボックス、フッター
    updateSpacerSize (state, { sectionId, partsContainerId, value }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      // スペーサーを取得
      const spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer')
      if (spacerParts.length === 0) {
        // スペーサーがない場合：スペーサーを追加
        // パーツの最大IDを取得
        let partMaxId = Math.max.apply(
          null,
          state.sections.map(section => {
            return Math.max.apply(
              null,
              section.parts_containers.map(partsContainer => {
                return Math.max.apply(
                  null,
                  partsContainer.parts.map(part => {
                    return part.id
                  })
                )
              })
            )
          })
        )
        // 更新対象のパーツを取得：テーブルID[1以外]
        const updateParts = partsContainer.parts.filter(part => part.table_id !== 1)

        // 全パーツのテーブルIDを更新：テーブルID + (テーブルID - 1)
        // テーブルID 2なら、2 + (2-1) = 3に、テーブルID 3なら、 3 + (3-1) = 5にする
        updateParts.forEach(updatePart => {
          const tableId = JSON.parse(JSON.stringify(updatePart.table_id))
          updatePart.table_id = tableId + (tableId - 1)
        })

        // パーツのひとつ前に、スペーサーを追加
        updateParts.forEach(updatePart => {
          // パーツのIDを増やす
          partMaxId++
          const spacerPart = {
            'id': partMaxId,
            'type': 'Spacer',
            'body': null,
            'table_id': updatePart.table_id - 1,
            'order_id': 1,
            'styles': null,
            'attrs': {
              'width': value,
              'height': value
            }
          }
          partsContainer.parts.push(spacerPart)
        })

        // テーブル数を更新：テーブル数 = パーツ数
        partsContainer.table_count = partsContainer.parts.length

        // table_id 順にソート
        partsContainer.parts.sort((a, b) => {
          if (a.table_id < b.table_id) return -1
          if (a.table_id > b.table_id) return 1
          return 0
        })
      } else {
        // スペーサーがある場合：大きさを更新
        spacerParts.forEach(spacerPart => {
          spacerPart.attrs['width'] = value
          spacerPart.attrs['height'] = value
        })
      }
    },
    updateImageTextSpacerSize (state, { sectionId, partsContainerId, value }) {
      const partsContainer = state.sections.find(section => section.id === sectionId).parts_containers.find(partsContainer => partsContainer.id === partsContainerId)
      // スペーサーを取得
      const spacerParts = partsContainer.parts.filter(part => part.type === 'Spacer' && part.order_id === 1)
      if (spacerParts.length === 0) {
        // スペーサーがない場合：スペーサーを追加
        // パーツの最大IDを取得
        let partMaxId = Math.max.apply(
          null,
          state.sections.map(section => {
            return Math.max.apply(
              null,
              section.parts_containers.map(partsContainer => {
                return Math.max.apply(
                  null,
                  partsContainer.parts.map(part => {
                    return part.id
                  })
                )
              })
            )
          })
        )
        // 最大のテーブルIDを取得
        const maxTableId = Math.max.apply(
          null,
          partsContainer.parts.map(part => {
            return part.table_id
          })
        )
        // スペーサーを作成
        let spacerTableId = 0
        let spacerParts = []
        for (let tableCount = 2; tableCount <= maxTableId; tableCount++) {
          // パーツIDを増やす
          partMaxId++
          // テーブルID：スペーサーパーツは2の倍数のテーブルに入る
          spacerTableId = spacerTableId + 2
          // パーツ作成：この段階ではまだパーツコンテナに追加しない
          const addSpacerPart = {
            'id': partMaxId,
            'type': 'Spacer',
            'body': null,
            'table_id': spacerTableId,
            'order_id': 1,
            'styles': null,
            'attrs': {
              'width': value,
              'height': value
            }
          }
          spacerParts.push(addSpacerPart)
        }

        // 既存パーツのテーブルIDを更新：[今のテーブルID + 今のテーブルID - 1]
        // 今のテーブルIDが[2]なら、[2 + 2 - 1] = 3 になる
        const tableIdUpdateParts = partsContainer.parts.filter(part => part.table_id !== 1)
        tableIdUpdateParts.forEach(part => {
          part.table_id = Number(part.table_id) + Number(part.table_id) - 1
        })

        // パーツコンテナにスペーサーを追加
        spacerParts.forEach(spacerPart => {
          partsContainer.parts.push(spacerPart)
        })

        // table_id order_id 順にソート
        partsContainer.parts.sort((a, b) => {
          if (a.table_id < b.table_id) return -1
          if (a.table_id > b.table_id) return 1
          if (a.order_id < b.order_id) return -1
          if (a.order_id > b.order_id) return 1
          return 0
        })

        // スペーサー追加後の、最大のテーブルIDを取得：一番最後のテーブルID
        const updateTableCount = partsContainer.parts.slice(-1)[0].table_id
        partsContainer.table_count = updateTableCount
      } else {
        // スペーサーがある場合：大きさを更新
        spacerParts.forEach(spacerPart => {
          spacerPart.attrs['width'] = value
          spacerPart.attrs['height'] = value
        })
      }
    },
    // エディターのメッセージを表示
    showMessageToast (state, messageToast) {
      // messageToast: {
      //  kind: success/error,
      //  message: 表示するメッセージ
      // }
      state.status.messageToasts.push({
        ...messageToast
      })
      setTimeout(
        () => this.commit('shiftMessageToast'),
        constants.toast.showTime
      )
    },
    // エディターのメッセージをshift
    shiftMessageToast (state) {
      state.status.messageToasts.shift()
    },
    // エディターのメッセージ配列をクリア
    clearMessageToast (state) {
      state.status.messageToasts = []
    },
    // パーツコンテナドラッグ時のアシスト切り替え
    toggleDragPartsContainerAssist (state, { showState }) {
      state.status.showDragPartsContainerAssist = showState
    },
    // パーツコンテナドラッグ時のホバーセクションの style 切り替え
    setDragPartsContainerActiveSection (state, { sectionId }) {
      state.status.dragPartsContainerActiveSection = sectionId
    },
    // リンクチェッカーが表示されたかどうか
    openedLinkChecker (state) {
      state.status.opendLinkChecker = true
    },
    resetOpenedLinkChecker (state) {
      state.status.opendLinkChecker = false
    },
    // TinyMCE のリンク配列をセット
    setTinyMceHrefs (state, { hrefs }) {
      // TinyMCE 内リンクをエスケープしない対応で TinyMCE 外に出している
      state.status.tinyMceHrefs = hrefs
    },
    // TinyMCE のリンク配列を新規追加
    insertTinyMceHrefs (state, { uuid, newHref }) {
      state.status.tinyMceHrefs.push({
        uuid: uuid,
        href: newHref,
      })
    },
    // リンクチェック用
    updateContentLink (state, { sectionIndex, partsContainerIndex, partIndex, partType, insidePartOrder, newHref }) {
      const section = state.sections[sectionIndex]
      if (!section) return
      const partsContainer = section.parts_containers[partsContainerIndex]
      if (!partsContainer) return
      const part = partsContainer.parts[partIndex]
      if (['Text', 'BoxedText', 'Footer', 'Code'].includes(part.type)) {
        let el = document.createElement('dummyTag')
        el.innerHTML = part.body
        if (el.getElementsByTagName('a')[insidePartOrder]) {
          el.getElementsByTagName('a')[insidePartOrder].href = newHref
        }
        // XXX コードパーツ タグ編集に同じ処理があるので、できれば共通化したい
        // 変換前のaタグからURLを取り出す
        const links = el.getElementsByTagName('a')
        let linksArray = []
        links.forEach(link => {
          if (link.hasAttribute('href')) linksArray.push(link.getAttribute('href'))
        })
        // &が変換される
        let newBody = JSON.parse(JSON.stringify(el.innerHTML))
        // 置換対象のURLを取り出す：match[1]にURL部分が入る
        const hrefRegex = /<a[^>]*href=["']?([^"']+)["']?.*?>/g
        let matches = [...newBody.matchAll(hrefRegex)]
        // 変換後のURL部分を、変換前URLで置換
        if (matches.length === linksArray.length) {
          let matchStartIndexDiff = 0 // 置換前と後で開始位置の差分が出るため、調整用
          matches.forEach((match, index) => {
            const replaceUrl = linksArray[index]
            newBody = replaceStringFromSpecifcIndex(
              newBody,
              match[1], // 置換対象
              replaceUrl,
              match.index - matchStartIndexDiff // 置換対象を探す開始位置
            )
            // 文字数の差分を追加
            matchStartIndexDiff += match[1].length - replaceUrl.length
          })
        }
        part.body = newBody
      }
      if (['Image', 'Button', 'SnsIcon'].includes(part.type)) {
        let linkKind = ''
        let snsName = ''
        if (newHref.startsWith('mailto:')) {
          // ディープコピー
          const emailIconConst = JSON.parse(JSON.stringify(constants.emailIcon))
          linkKind = emailIconConst.attrs['data-link-kind']
          snsName = emailIconConst.attrs['data-sns-name'].toLowerCase() + 'Icon'
        } else if (newHref.startsWith('tel:')) {
          // ディープコピー
          const telIconConst = JSON.parse(JSON.stringify(constants.telIcon))
          linkKind = telIconConst.attrs['data-link-kind']
          snsName = telIconConst.attrs['data-sns-name'].toLowerCase() + 'Icon'
        } else {
          // ディープコピー
          const websiteIconConst = JSON.parse(JSON.stringify(constants.websiteIcon))
          linkKind = websiteIconConst.attrs['data-link-kind']
          snsName = websiteIconConst.attrs['data-sns-name'].toLowerCase() + 'Icon'
        }

        // リンクの種類が変わった場合のみ Vuex のデータを更新する
        if (part.attrs['data-link-kind'] !== linkKind) {
          part.attrs['data-link-kind'] = linkKind
          // 実在している SNS 名を持たない場合のみフォローボタンのアイコン画像を変更する
          if (
            part.type === 'SnsIcon' &&
            (
              ['Website', 'Email', 'Tel'].includes(part.attrs['data-sns-name']) ||
              ['email', 'tel'].includes(linkKind)
            )
          ) {
            const newSnsIcon = JSON.parse(JSON.stringify(constants[snsName]))
            // アイコンスタイルを取得
            let srcType = partsContainer.src_type
            if ((newSnsIcon.attrs['data-sns-name'] === 'LINE') || (newSnsIcon.attrs['data-sns-name'] === 'X' && partsContainer.src_type === 'black')) {
              // LINEはcolorのみ,X(black)はcolorと共通
              srcType = 'color'
            }

            // attrsを上書きする
            part.attrs = {
              ...newSnsIcon.attrs,
              width: part.attrs['width'],
              height: part.attrs['height'],
              src: constants[snsName].url[srcType],
              'data-display-sns': part.attrs['data-display-sns'],
            }
          }
        }

        if (part.attrs && part.attrs.href) {
          if (partsContainer.type === 'Share') {
            // シェアボタンの場合、パーツ全てに同じリンクを設定
            partsContainer.parts.forEach(part => {
              part.attrs.href = newHref
            })
          } else {
            part.attrs.href = newHref
          }
        }
      }
    },
    mergeContentImages (state, images) {
      state.contentImages = { ...state.contentImages, ...images }
    },
    updateContentImages (state, images) {
      state.contentImages = images
    },
    updateContentTmpUuid (state, tmpUuid) {
      state.contentTmpUuid = tmpUuid
    },
    updateContentInfoValidations (state, contentInfoValidations) {
      state.contentInfoValidations = contentInfoValidations
    },
    updateContentTags (state, tags) {
      // タグ新規作成時用にキーだけ入れておく
      tags.forEach(tag => {
        if (!tag.tagId || !tag.contentTagMapId) {
          tag.tagId = null
          tag.contentTagMapId = null
        }
      })
      state.contentTags = tags
    },
    mergeMessagePopup (state, data) {
      state.status.messagePopup = {
        ...state.status.messagePopup,
        ...data
      }
    },
    showErrorMessagePopup (state, messages) {
      state.status.messagePopup = {
        isShow: true,
        isSuccess: false,
        title: '',
        messages,
      }
    },
    updateEditDiff (state, { isUpdated, storeHashBeforeEdit }) {
      state.editDiff = {
        isUpdated,
        storeHashBeforeEdit,
      }
    },
    // コードパーツ HTMLで未使用の画像がstoreに残っていたら削除
    deleteCodePartUnUseImage (state, sections) {
      for (const section of sections) {
        for (const partsContainer of section.parts_containers) {
          for (const part of partsContainer.parts) {
            // コードパーツで、attrsのある場合のみ続行
            if (part.type !== 'Code') continue
            if (!part.attrs || part.attrs.length === 0) continue
            // 画像独自タグのあるブロックを取得
            const HTMLObject = new DOMParser().parseFromString(part.body, 'text/html')
            const HTMLCollection = HTMLObject.body.getElementsByTagName('*')
            let editNodeList = [] // 画像独自タグのあるブロック
            for (let i = 0; i < HTMLCollection.length; i++) {
              const HTMLCollect = HTMLCollection[i]
              if (HTMLCollect.hasAttribute(constants.part.code.imageAttrName)) {
                editNodeList.push(HTMLCollect)
              }
            }

            if (editNodeList.length === 0) {
              // HTML内に画像の独自属性が存在しない場合、attrsごと削除
              Vue.delete(part, 'attrs')
            } else {
              let deleteAttrImageKeys = [] // 削除対象の画像キー
              part.attrs.forEach(partAttr => {
                // storeの画像キーと、HTMLの画像キーを付き合わせる
                let partAttrImageKey = null
                for (let i = 0; i < editNodeList.length; i++) {
                  const editNode = editNodeList[i]
                  const imageKey = editNode.hasAttribute('data-image-key') ? editNode.getAttribute('data-image-key') : null
                  if (imageKey === partAttr['data-image-key']) {
                    partAttrImageKey = partAttr['data-image-key']
                  }
                }
                // 同じ画像キーがない場合、削除対象の配列に追加しておく
                if (!partAttrImageKey) {
                  deleteAttrImageKeys.push(partAttr['data-image-key'])
                }
              })
              // 削除対象の画像キーを使って、storeから削除する
              if (deleteAttrImageKeys.length === 0) continue
              deleteAttrImageKeys.forEach(deleteAttrImageKey => {
                const attrIndex = part.attrs.findIndex(
                  attr => attr['data-image-key'] === deleteAttrImageKey
                )
                if (attrIndex >= 0) Vue.delete(part.attrs, attrIndex)
              })
            }
          }
        }
      }
    },
    clearTagLikeEmailInput (state) {
      state.tagLikeEmailInput = {
        emails: [],
        text: '',
      }
    },
    updateTagLikeEmailInputEmails (state, emails) {
      state.tagLikeEmailInput.emails = JSON.parse(JSON.stringify(emails))
    },
    updateTagLikeEmailInputText (state, text) {
      state.tagLikeEmailInput.text = text
    },
    updateEcV2PersonalizeItems (state, items) {
      state.ecV2PersonalizeItems = items
    },
  }
})
